/**
 * useFeatures Hook
 *
 * TanStack Query hook for fetching and managing repository features
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Feature } from '@forgenetwork/types';

/**
 * Response from features API endpoint
 */
interface FeaturesResponse {
  features: Feature[];
  total: number;
  repositoryId: string;
}

/**
 * Response from feature detail API endpoint
 */
interface FeatureDetailResponse {
  feature: Feature;
}

/**
 * Request to trigger feature extraction
 */
interface ExtractFeaturesRequest {
  repositoryId: string;
  force?: boolean;
}

/**
 * Fetch all features for a repository
 *
 * @param repositoryId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with features array
 *
 * @example
 * ```tsx
 * function FeaturesList() {
 *   const { data, isLoading } = useFeatures('repo-123');
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data?.features.map(feature => (
 *         <FeatureCard key={feature.id} feature={feature} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFeatures(repositoryId: string, enabled = true) {
  return useQuery<FeaturesResponse>({
    queryKey: ['features', repositoryId],
    queryFn: async () => {
      return await api.get<FeaturesResponse>(`/repositories/${repositoryId}/features`);
    },
    enabled: enabled && !!repositoryId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch a single feature by ID
 *
 * @param repositoryId - The repository ID
 * @param featureId - The feature ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with feature detail
 *
 * @example
 * ```tsx
 * function FeatureDetail({ featureId }: { featureId: string }) {
 *   const { data, isLoading } = useFeature('repo-123', featureId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return <div>{data?.feature.name}</div>;
 * }
 * ```
 */
export function useFeature(repositoryId: string, featureId: string, enabled = true) {
  return useQuery<FeatureDetailResponse>({
    queryKey: ['feature', repositoryId, featureId],
    queryFn: async () => {
      return await api.get<FeatureDetailResponse>(
        `/repositories/${repositoryId}/features/${featureId}`
      );
    },
    enabled: enabled && !!repositoryId && !!featureId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Mutation to trigger feature extraction for a repository
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * function ExtractButton({ repositoryId }: { repositoryId: string }) {
 *   const extractFeatures = useExtractFeatures();
 *
 *   return (
 *     <button
 *       onClick={() => extractFeatures.mutate({ repositoryId })}
 *       disabled={extractFeatures.isPending}
 *     >
 *       {extractFeatures.isPending ? 'Extracting...' : 'Extract Features'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useExtractFeatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ExtractFeaturesRequest) => {
      return await api.post<FeaturesResponse>(
        `/repositories/${request.repositoryId}/features/extract`,
        { force: request.force }
      );
    },
    onSuccess: (data) => {
      // Invalidate features cache to refetch
      queryClient.invalidateQueries({
        queryKey: ['features', data.repositoryId],
      });
      // Also invalidate repository cache as it may have updated analysis status
      queryClient.invalidateQueries({
        queryKey: ['repository', data.repositoryId],
      });
    },
  });
}

/**
 * Get cached features without triggering a fetch
 *
 * @param repositoryId - The repository ID
 * @returns Cached features or undefined
 *
 * @example
 * ```tsx
 * function QuickFeatureCount({ repositoryId }: { repositoryId: string }) {
 *   const features = useCachedFeatures(repositoryId);
 *
 *   return <span>{features?.length ?? 0} features</span>;
 * }
 * ```
 */
export function useCachedFeatures(repositoryId: string): Feature[] | undefined {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<FeaturesResponse>(['features', repositoryId]);
  return cached?.features;
}
